import { useEffect, useMemo, useState } from "react";
import { samplePreview, type PfpProject } from "@/pfp/generator";

const COUNT = 12;

/**
 * Renders a small grid of randomized sample tokens. Regenerates when the
 * `nonce` changes — the parent owns the regen trigger so it can sync with
 * the main RollPreview if it wants to.
 */
export function SampleGallery({ project, nonce }: { project: PfpProject; nonce: number }) {
  const [samples, setSamples] = useState<(string | null)[]>(() => Array<string | null>(COUNT).fill(null));

  // Compute a per-tile seed array from the nonce so each tile rolls
  // independently. Recomputed in lockstep with the nonce.
  const seeds = useMemo(
    () => Array.from({ length: COUNT }, (_, i) => nonce * 1_000 + i + 1),
    [nonce],
  );

  useEffect(() => {
    let cancelled = false;
    setSamples(Array<string | null>(COUNT).fill(null));
    void (async () => {
      const next: (string | null)[] = Array<string | null>(COUNT).fill(null);
      for (let i = 0; i < COUNT; i++) {
        if (cancelled) return;
        const seed = seeds[i] ?? i + 1;
        try {
          // Tiny tiles — 128px is plenty at the displayed size and
          // keeps the 12-tile grid under ~100 KB of data URLs total.
          const result = await samplePreview(project, { seed, targetSize: 128 });
          next[i] = result?.dataUrl ?? null;
        } catch {
          next[i] = null;
        }
        // Push partial updates so the grid fills progressively.
        if (!cancelled) setSamples([...next]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [project, seeds]);

  const empty = project.layers.length === 0 || project.layers.every((l) => l.traits.length === 0);
  if (empty) return null;

  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between">
        <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Samples
        </h3>
        <span className="text-[10px] text-muted-foreground">{COUNT} rolls</span>
      </div>
      <div className="grid grid-cols-3 gap-1.5 sm:grid-cols-4">
        {samples.map((src, i) => (
          <div
            key={i}
            className="aspect-square overflow-hidden rounded-md border border-border bg-muted"
          >
            {src ? (
              <img src={src} alt={`Sample ${i + 1}`} className="h-full w-full object-cover" />
            ) : (
              <div className="h-full w-full animate-pulse" aria-hidden="true" />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
